/**
 * Documents over MCP: the tree, one document, subpages, and the writes.
 *
 * Four verified hazards shape this file, and none of them is obvious from the
 * store's API:
 *
 * 1. **`writeBoard` is a whole-scope diff** — irrelevant here, but the same
 *    class applies to documents: `data` is a full REPLACE, never a merge, so
 *    every write has to carry the fields it wants to keep.
 * 2. **No version is exposed to JS**, so concurrency rides a CONTENT HASH.
 *    `extension_records_list_meta` returns `rev` (16 hex of
 *    `sha256(canonical_json(data))`, computed in Rust so there is one
 *    implementation), and `extension_records_cas_upsert` compares it inside
 *    one transaction — no TOCTOU window.
 * 3. **The open editor wins.** A store write to a document currently open in
 *    the Lexical editor is invisible to it and is overwritten by its next
 *    autosave. So if a live editor exists we drive it — but we **must NOT
 *    navigate**. The AI path calls `navigateToPanel` + `openDocumentTab`
 *    because a person asked it to; an external agent doing that yanks the
 *    user's window to a document they did not ask to see. This divergence is
 *    deliberate and is pinned by a test so nobody later "aligns" it.
 * 4. **Append is not read-concat.** The AI path reads then concatenates, which
 *    is a lost update. Here append reads, hashes and CAS-writes in a bounded
 *    retry loop, and returns `conflict` if it still loses.
 *
 * And the trap that dominates everything: an outbox op over
 * `OUTBOX_ITEM_MAX_BYTES` (250 000 since 14.10.2026) is dropped with only a `log::error!`, so an
 * oversize document looks saved, reads back fine here, and never syncs again.
 * Rust pre-checks and returns `too_large`; this file turns that into a tool
 * error rather than letting it look like success.
 */
import { invoke } from '@tauri-apps/api/core';
import {
  countOwnDocuments,
  listTree,
  readDocument,
  createDocument,
  createFolder,
  renameNode,
  recordsContext,
  padKey,
  markDocumentAi,
  documentBodyRev,
  saveDocumentBody,
  snapshotFromHtml,
} from '../../extensions/bundled/documents/documentsStore';
import { largestBlockBytes } from '../../extensions/bundled/documents/docBlocks';
import { OUTBOX_ITEM_MAX_BYTES } from '../../records/syncLimits';
import { aiCreatedMarks, aiEditedMarks, mcpSource } from '../../services/ai/aiAuthorship';
import { docBodyData } from '../../extensions/bundled/documents/docBodyCodec';
import { isOverSyncHint } from '../boardRecords';
import { markdownToHtml } from '../../utils/markdownToHtml';
import { sanitizeRichTextHtml } from '../../utils/sanitizeHtml';
import { getAiDocEditor } from '../../services/aiExtensionBridges';
import { renderBody } from '../markdown';
import { descriptionHtmlFor } from '../taskDescription';
import { attachmentTags } from './commentAttachments';
import { documentToReadableMarkdown } from '../../utils/docMarkdown/readable';
import { documentUri, resourceEntry, resourceLink } from '../resources';
import { toDocScope, PERSONAL } from '../scopes';
import { resolveDocScopes, toListScope, uriScopeOf, isWorkspaceScope, refuseUnwritableDocScope } from '../docScopes';
import { mcpError } from '../errors';
import { capRefusalFor } from '../../plans/capRefusal';

const EXTENSION_ID = 'documents';
/** New files one `document_write` may add (each up to 48 MB, uploaded first). */
export const MAX_DOCUMENT_ATTACHMENTS = 8;
const BODY_SUFFIX = '::body';

/** `[{id, record_type, updated_at, rev, ...}]` for one scope, keyed by id. */
async function metaByIdFor(wireScope) {
  const ctx = recordsContext(toDocScope(wireScope));
  if (!ctx) return new Map();
  const rows = await invoke('extension_records_list_meta', {
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: null,
    // Documents keep one tree per scope, so the whole scope IS the answer here.
    // Stated rather than omitted since the command gained the parameter in
    // phase 2 — boards need it, documents deliberately do not use it.
    scopeId: null,
  });
  return new Map((rows || []).map((r) => [r.id, r]));
}

/**
 * When each document's body last changed, from one meta read: its docbody
 * row or any of its block rows (a body stored per block — docBlocks.js — no
 * longer touches the docbody row on every edit).
 */
function bodyEditTimes(meta) {
  const out = new Map();
  for (const r of meta.values()) {
    let docId = null;
    if (r.record_type === 'docblock') docId = r.parent_id;
    else if (typeof r.id === 'string' && r.id.endsWith(BODY_SUFFIX)) docId = r.id.slice(0, -BODY_SUFFIX.length);
    if (!docId || !Number.isFinite(r.updated_at)) continue;
    out.set(docId, Math.max(out.get(docId) || 0, r.updated_at));
  }
  return out;
}

/** True when one record of this body would be over what a synced record may hold. */
function overSyncSize(doc) {
  if (doc.blockMode) return largestBlockBytes(doc.id, doc.bodyLexical) > OUTBOX_ITEM_MAX_BYTES - 2000;
  return isOverSyncHint(docBodyData(doc.id, doc.bodyHtml, doc.bodyLexical));
}

export async function documentsTree({ scope }) {
  const { scopes, consulted, unavailable } = await resolveDocScopes(scope);
  const nodes = [];
  const resources = [];
  const links = [];
  const reached = [];
  const missed = [...unavailable];

  for (const wire of scopes) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const [tree, meta] = await Promise.all([
        // `personal` lists the Personal workspace only; each workspace is
        // its own entry in `scopes` (docScopes.js), so a node shows once.
        listTree(toListScope(wire)),
        metaByIdFor(wire),
      ]);
      reached.push(wire);
      const edited = bodyEditTimes(meta);
      for (const node of tree) {
        nodes.push({
          id: node.id,
          type: node.type,
          name: node.name,
          parentId: node.parentId,
          sortKey: node.sortKey,
          scope: wire,
        });
        if (node.type !== 'document') continue;
        const uri = documentUri(uriScopeOf(wire), node.id);
        resources.push({
          ...resourceEntry({
            uri,
            name: node.name || node.id,
            title: node.name || undefined,
            mimeType: 'text/markdown',
          }),
          // Read by `mcp::transport::resources_list` for annotations; a
          // resource without a lastModified cannot be sorted by recency, which
          // is most of what makes documents a signal rather than a pile.
          updatedAt: edited.get(node.id) ?? meta.get(node.id)?.updated_at ?? null,
        });
        links.push(resourceLink(uri, node.name || node.id));
      }
    } catch {
      // A scope that cannot be read is REPORTED, never silently dropped.
      missed.push(wire);
    }
  }
  return {
    nodes,
    resources,
    resourceLinks: links,
    scopesConsulted: reached.length ? reached : consulted,
    scopesUnavailable: missed,
  };
}

/**
 * Markdown from the editor's own blocks when there are any (nested lists,
 * tables, code language, page links as apiboo:// URIs). HTML-only bodies
 * (written by MCP / AI, which clear the blocks) — or blocks the readable
 * writer cannot say — read through the HTML path. Never the raw blocks.
 */
function bodyText(doc, format) {
  if ((format || 'markdown') === 'markdown' && doc.bodyLexical?.blocks?.length) {
    try {
      return documentToReadableMarkdown(doc.bodyLexical).trim();
    } catch {
      /* fall through to the HTML */
    }
  }
  return renderBody(doc.bodyHtml, format);
}

export async function documentRead({ document_id: documentId, scope, format }) {
  const wire = scope || PERSONAL;
  const doc = await readDocument(String(documentId), toDocScope(wire));
  if (!doc) return mcpError('not_found', 'No document with that id in this scope.');
  const meta = await metaByIdFor(wire);
  const folderMeta = meta.get(doc.id);
  return {
    id: doc.id,
    title: doc.title,
    format: format || 'markdown',
    // Markdown from the editor's own blocks when there are any (nested lists,
    // tables, code language, page links as apiboo:// URIs); HTML-only bodies
    // (written by MCP / AI, which clear the blocks) keep the regex path.
    text: bodyText(doc, format),
    // A hash of the body's content (blocks or HTML), not of one row: with the
    // body stored per block, the docbody row no longer changes on every edit.
    rev: await documentBodyRev(doc),
    updatedAt: bodyEditTimes(meta).get(doc.id) ?? folderMeta?.updated_at ?? null,
    scope: wire,
    uri: documentUri(uriScopeOf(wire), doc.id),
    // An already-oversize body is reported rather than discovered later: it is
    // saved here and will never reach the user's other devices.
    // Measured on the record as the next save stores it (HTML + compact blocks).
    syncable: !overSyncSize(doc),
    // `bodyLexical` is deliberately absent from this object — see the header.
  };
}

export async function documentSubpages({ document_id: documentId, scope }) {
  const wire = scope || PERSONAL;
  const tree = await listTree(toDocScope(wire));
  const children = tree.filter((n) => n.parentId === String(documentId));
  return {
    parentId: String(documentId),
    scope: wire,
    nodes: children.map((n) => ({
      id: n.id,
      type: n.type,
      name: n.name,
      sortKey: n.sortKey,
      scope: wire,
    })),
    resourceLinks: children
      .filter((n) => n.type === 'document')
      .map((n) => resourceLink(documentUri(uriScopeOf(wire), n.id), n.name || n.id)),
  };
}

/**
 * A create into a workspace (card 4.4): the workspace must exist, and a parent
 * must be in it — a child follows its root's workspace, so a parent elsewhere
 * would put the new node somewhere the caller did not name. `null` = go.
 */
async function refuseWorkspaceCreate(wire, parentId) {
  if (!isWorkspaceScope(wire)) return null;
  const refusal = await refuseUnwritableDocScope(wire);
  if (refusal) return refusal;
  if (parentId == null) return null;
  const tree = await listTree(toDocScope(wire));
  if (tree.some((n) => n.id === String(parentId))) return null;
  return mcpError('not_found', 'No document or folder with that id in this workspace.', { parentId: String(parentId) });
}

export async function documentCreateFolder({ name, parent_id: parentId, scope }) {
  const wire = scope || PERSONAL;
  const refusal = await refuseWorkspaceCreate(wire, parentId);
  if (refusal) return refusal;
  const folder = await createFolder(
    { parentId: parentId ?? null, name: String(name), sortKey: padKey(Date.now() % 1e12) },
    toDocScope(wire)
  );
  return { id: folder.id, type: 'folder', name: folder.name, scope: wire };
}

export async function documentCreate({ title, parent_id: parentId, content, scope }, ctx = {}) {
  const wire = scope || PERSONAL;
  const refusal = await refuseWorkspaceCreate(wire, parentId);
  if (refusal) return refusal;
  // Plans v2: a document of the user's OWN (personal, or one of their
  // document workspaces `personal/<id>`, both in countOwnDocuments) counts
  // against the plan's local cap; a team's documents belong to the team.
  if (wire === PERSONAL || isWorkspaceScope(wire)) {
    const capped = await capRefusalFor('documents', countOwnDocuments);
    if (capped) return mcpError('plan_limit', capped, { requiredPlan: 'basic' });
  }
  const bodyHtml = content ? toBodyHtml(content, 'markdown') : '';
  const created = await createDocument(
    { parentId: parentId ?? null, title: String(title), sortKey: padKey(Date.now() % 1e12) },
    toDocScope(wire),
    // Never route this one to the device (cloud/local items, card C5): there
    // is no `device` wire scope, so the body write and the returned URI would
    // both point at a scope this protocol cannot name. An MCP create at the
    // Free limit is refused by the server on push, as it was before.
    // AI Act Art. 50(2): an external agent wrote it — `ai_generated` plus
    // `ai_source: 'mcp'` and the client's id (services/ai/aiAuthorship.js).
    { allowDeviceFallback: false, aiMarks: aiCreatedMarks(bodyHtml, mcpSource(ctx?.caller)) }
  );
  if (content) {
    const written = await writeBody(wire, created.id, bodyHtml, null);
    if (written.error) return written;
  }
  return {
    id: created.id,
    type: 'document',
    title: created.name,
    scope: wire,
    uri: documentUri(uriScopeOf(wire), created.id),
  };
}

export async function documentRename({ node_id: nodeId, name, scope }) {
  const wire = scope || PERSONAL;
  const tree = await listTree(toDocScope(wire));
  const node = tree.find((n) => n.id === String(nodeId));
  if (!node) return mcpError('not_found', 'No document or folder with that id in this scope.');
  await renameNode(node, String(name), toDocScope(wire));
  return { id: node.id, type: node.type, name: String(name), scope: wire };
}

export async function documentWrite({
  document_id: documentId,
  content,
  mode,
  format,
  expected_rev: expectedRev,
  scope,
  attachments,
}, ctx = {}) {
  const wire = scope || PERSONAL;
  const docId = String(documentId);
  const append = mode === 'append';
  // AI Act Art. 50(2): every landed write marks the document as edited by
  // this MCP client; the body's rev (expected_rev) is not affected. The body
  // has landed by then, so a failed mark must not turn it into an error.
  const markEdited = async (bodyHtml) => {
    try {
      await markDocumentAi(docId, aiEditedMarks(bodyHtml, mcpSource(ctx?.caller)), toDocScope(wire));
    } catch (err) {
      if (process.env.NODE_ENV !== 'test') console.warn('[mcp] AI mark failed:', err);
    }
  };

  // New screenshots/videos (MCP 10): references the media upload tool
  // returned for THIS document, checked before anything is read or written.
  let added = [];
  if (attachments !== undefined) {
    const built = attachmentTags(attachments, MAX_DOCUMENT_ATTACHMENTS, 'call', {
      docUid: docId,
      // A workspace document uploads under personal (the same server folder).
      scope: uriScopeOf(wire),
      hint: 'This file was uploaded for another document or task. Upload it again with this document_id (and scope), then pass that attachment.',
    });
    if (built.error) return mcpError('invalid_argument', built.error.message, { field: built.error.field });
    added = built.tags;
  }

  const doc = await readDocument(docId, toDocScope(wire));
  if (!doc) return mcpError('not_found', 'No document with that id in this scope.');
  // `document_read` never hands out image keys (card MCP 1): an image is a
  // "🖼 Image: name" line (markdown) or a keyless <img> (html). Written back,
  // each image of THIS document gets its own key again, as a task description
  // does (taskDescription.js) — keys the agent sends are stripped. A markdown
  // append starts from nothing: an image line there stays text, so a markdown
  // append never copies an image the document already shows. While the
  // document is open, its images are the ones in the editor (pasted and not
  // saved yet included), not the stored body's.
  // THE live-editor branch is captured here, and NEVER navigates — see the header.
  const liveEditor = getAiDocEditor(docId);
  const stored = (liveEditor && liveEditor.getBody?.()) || doc.bodyHtml || '';
  const own = append && format !== 'html' ? '' : stored;
  const addition = descriptionHtmlFor(content, format === 'html' ? 'html' : 'markdown', own, added);

  // Replacing existing prose REQUIRES a rev: without one an agent working from
  // a stale read silently destroys whatever the user typed since.
  if (!append && !expectedRev) {
    return mcpError(
      'expected_rev_required',
      'Replacing a document body requires expected_rev from a preceding document_read.'
    );
  }

  if (liveEditor) {
    const current = append ? liveEditor.getBody?.() || '' : '';
    liveEditor.applyBody(`${current}${addition}`);
    await markEdited(`${current}${addition}`);
    return { id: docId, scope: wire, mode: append ? 'append' : 'replace', via: 'editor' };
  }

  if (!append) {
    const replaced = await writeBody(wire, docId, addition, expectedRev);
    if (!replaced.error) await markEdited(addition);
    return replaced;
  }

  // Append: read → hash → CAS, bounded. Not read-concat, which loses updates.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const fresh = await readDocument(docId, toDocScope(wire));
    if (!fresh) return mcpError('not_found', 'No document with that id in this scope.');
    // eslint-disable-next-line no-await-in-loop
    const rev = await documentBodyRev(fresh);
    // eslint-disable-next-line no-await-in-loop
    const appended = `${fresh.bodyHtml || ''}${addition}`;
    // eslint-disable-next-line no-await-in-loop
    const result = await writeBody(wire, docId, appended, rev);
    if (!result.error) {
      // eslint-disable-next-line no-await-in-loop
      await markEdited(appended);
      return result;
    }
    if (result.error !== 'conflict') return result;
  }
  return mcpError('conflict', 'The document changed while appending; read it again and retry.');
}

/**
 * The one write path: the body is REPLACED by `bodyHtml`, through the store
 * (saveDocumentBody turns it into blocks and writes only those that change).
 * `expectedRev` is the body's content hash, checked here and again by the
 * store against the very rows it writes from.
 */
async function writeBody(wire, docId, bodyHtml, expectedRev) {
  const scope = toDocScope(wire);
  const ctx = recordsContext(scope);
  if (!ctx) return mcpError('unavailable', 'Documents are unavailable for this scope.');
  try {
    const snapshot = await snapshotFromHtml(docId, bodyHtml);
    // One block must fit in a synced record (or, stored whole, the body).
    const bytes = snapshot ? largestBlockBytes(docId, snapshot) : JSON.stringify(docBodyData(docId, bodyHtml, null)).length;
    if (bytes > OUTBOX_ITEM_MAX_BYTES - 2000) return fromRustError(`too_large:${bytes}:${OUTBOX_ITEM_MAX_BYTES}`);
    const current = await readDocument(docId, scope);
    if (!current) return mcpError('not_found', 'No document with that id in this scope.');
    if (expectedRev != null) {
      const actual = await documentBodyRev(current);
      if (actual !== expectedRev) return fromRustError(`conflict:${actual}`);
    }
    // Checked once more by the store against the rows it writes from
    // (`conflict:<rev>` thrown, mapped below); unchanged blocks keep their ids.
    await saveDocumentBody(docId, { bodyHtml, bodyLexical: snapshot }, scope, { fromHtml: true, expectedRev });
    const rev = await documentBodyRev(await readDocument(docId, scope));
    return { id: docId, scope: wire, rev, via: 'store' };
  } catch (err) {
    return fromRustError(err);
  }
}

/**
 * Rust returns `conflict:<rev>` / `too_large:<bytes>:<cap>` and nothing else —
 * a fixed vocabulary, so no underlying error text is ever relayed to a model.
 */
export function fromRustError(err) {
  const text = typeof err === 'string' ? err : '';
  if (text.startsWith('conflict:')) {
    return mcpError(
      'conflict',
      'The document changed since you read it. Read it again and retry with the new rev.',
      { rev: text.slice('conflict:'.length) || null }
    );
  }
  if (text.startsWith('too_large:')) {
    const [, bytes, cap] = text.split(':');
    return mcpError(
      'document_too_large',
      'That body is too large to sync to the user\'s other devices, so it was not written. Split it into smaller documents.',
      { bytes: Number(bytes) || null, cap: Number(cap) || null }
    );
  }
  return mcpError('write_failed', 'The write could not be completed.');
}

function toBodyHtml(content, format) {
  const text = String(content ?? '');
  return sanitizeRichTextHtml(format === 'html' ? text : markdownToHtml(text));
}
